import React, { useState, useEffect, useMemo } from 'react';
import {
    Portal,
    Tooltip,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ClientService from '../../../Services/client.service';
import AutocompleteSelectFilter from '../AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './MonthStatusModal.module.scss';

const MySwal = withReactContent(Swal);

const MonthStatusModal = ({ open, onClose, caseId, selectedYear: initialYear, token, t, direction = 'rtl', onRefresh }) => {
    const [monthsStatus, setMonthsStatus] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [selectedYear, setSelectedYear] = useState(initialYear || new Date().getFullYear());

    // Generate year options (current year ± 5 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = useMemo(() => {
        const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
        const selected = Number(selectedYear);
        if (selected && !years.includes(selected)) {
            years.push(selected);
            years.sort((a, b) => a - b);
        }
        return years.map((year) => ({ value: year, label: String(year) }));
    }, [currentYear, selectedYear]);
    const selectedYearOption = yearOptions.find((option) => option.value === Number(selectedYear)) || null;

    useEffect(() => {
        if (initialYear) {
            setSelectedYear(initialYear);
        }
    }, [initialYear]);

    useEffect(() => {
        if (open && caseId && selectedYear && token) {
            fetchMonthsStatus();
        }
    }, [open, caseId, selectedYear, token]);

    const fetchMonthsStatus = async () => {
        setLoading(true);
        try {
            const data = await ClientService.GetYearMonthsStatus(caseId, selectedYear, token);
            setMonthsStatus(data);
        } catch (error) {
            console.error('Error fetching months status:', error);
            toast.error(t('ErrorFetchingMonthsStatus'));
        } finally {
            setLoading(false);
        }
    };

    const handleToggleMonth = async (month, currentStatus, monthName) => {
        // Show SweetAlert confirmation
        const result = await MySwal.fire({
            title: currentStatus ? t('ConfirmOpenMonth') : t('ConfirmCloseMonth'),
            html: `${currentStatus ? t('AreYouSureOpenMonth') : t('AreYouSureCloseMonth')}<br/><strong>${monthName} ${selectedYear}</strong>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('Yes'),
            cancelButtonText: t('No'),
            confirmButtonColor: currentStatus ? '#4caf50' : '#d32f2f',
            cancelButtonColor: '#757575',
        });

        if (!result.isConfirmed) {
            return;
        }

        setUpdating(true);
        try {
            const reportingMonthDate = new Date(selectedYear, month - 1, 10); // Day 10 of the month
            const response = await ClientService.UpdateReportingMonth(
                caseId,
                reportingMonthDate,
                !currentStatus,
                token
            );

            if (response.ok) {
                toast.success(
                    currentStatus 
                        ? t('MonthReportOpenedSuccess') 
                        : t('MonthReportClosedSuccess')
                );
                
                // Update local state
                setMonthsStatus(prevMonths =>
                    prevMonths.map(m =>
                        m.month === month
                            ? { ...m, isClosed: !currentStatus }
                            : m
                    )
                );

                // Refresh page data in the background
                if (onRefresh) {
                    onRefresh();
                }
            } else {
                const errorText = await response.text();
                toast.error(
                    currentStatus 
                        ? t('MonthReportNotOpened') + ': ' + errorText
                        : t('MonthReportNotClosed') + ': ' + errorText
                );
            }
        } catch (error) {
            console.error('Error updating month status:', error);
            toast.error(t('ErrorUpdatingMonthStatus'));
        } finally {
            setUpdating(false);
        }
    };

    const handleToggleYear = async (closeYear) => {
        const monthsToUpdate = monthsStatus.filter(m => (closeYear ? !m.isClosed : m.isClosed));

        if (monthsToUpdate.length === 0) {
            toast.info(closeYear ? t('AllMonthsAlreadyClosed') : t('AllMonthsAlreadyOpen'));
            return;
        }

        const result = await MySwal.fire({
            title: closeYear ? t('ConfirmCloseYear') : t('ConfirmOpenYear'),
            html: `${closeYear ? t('AreYouSureCloseYear') : t('AreYouSureOpenYear')}<br/><strong>${selectedYear}</strong>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('Yes'),
            cancelButtonText: t('No'),
            confirmButtonColor: closeYear ? '#d32f2f' : '#4caf50',
            cancelButtonColor: '#757575',
        });

        if (!result.isConfirmed) {
            return;
        }

        setUpdating(true);
        try {
            for (const month of monthsToUpdate) {
                const reportingMonthDate = new Date(selectedYear, month.month - 1, 10);
                const response = await ClientService.UpdateReportingMonth(
                    caseId,
                    reportingMonthDate,
                    closeYear,
                    token
                );

                if (!response.ok) {
                    const errorText = await response.text();
                    toast.error(
                        closeYear
                            ? t('YearReportNotClosed') + ': ' + errorText
                            : t('YearReportNotOpened') + ': ' + errorText
                    );
                    await fetchMonthsStatus();
                    return;
                }
            }

            toast.success(closeYear ? t('YearReportClosedSuccess') : t('YearReportOpenedSuccess'));
            setMonthsStatus(prevMonths =>
                prevMonths.map(m => ({ ...m, isClosed: closeYear }))
            );

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error updating year status:', error);
            toast.error(t('ErrorUpdatingMonthStatus'));
        } finally {
            setUpdating(false);
        }
    };

    const handleYearChange = (event, newValue) => {
        const next = newValue?.value ?? newValue;
        if (next == null || next === '') return;
        setSelectedYear(Number(next));
    };

    const getMonthName = (monthNumber) => {
        const date = new Date(selectedYear, monthNumber - 1, 1);
        return date.toLocaleDateString('he-IL', { month: 'short' });
    };

    const getClosedCount = () => monthsStatus.filter(m => m.isClosed).length;
    const getOpenCount = () => monthsStatus.filter(m => !m.isClosed).length;

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('MonthlyReportStatus')}</div>
                                <div />
                            </div>

                            <div className={styles.toolbar}>
                                <div className={styles.yearField}>
                                    <AutocompleteSelectFilter
                                        options={yearOptions}
                                        selectedValues={selectedYearOption}
                                        onChange={handleYearChange}
                                        label={t('Year')}
                                        placeholder={t('Year')}
                                        isMultiple={false}
                                        isDisabled={loading || updating}
                                        direction={direction}
                                    />
                                </div>

                                {!loading && monthsStatus.length > 0 && (
                                    <div className={styles.stats}>
                                        <span className={`${styles.chip} ${styles.chipOpen}`}>
                                            <LockOpenIcon fontSize="small" />
                                            {getOpenCount()} {t('Open')}
                                        </span>
                                        <span className={`${styles.chip} ${styles.chipClosed}`}>
                                            <LockIcon fontSize="small" />
                                            {getClosedCount()} {t('Closed')}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {!loading && monthsStatus.length > 0 && (
                                <div className={styles.yearActions}>
                                    <button
                                        type="button"
                                        className={styles.closeYear}
                                        disabled={updating || getOpenCount() === 0}
                                        onClick={() => handleToggleYear(true)}
                                    >
                                        <LockIcon fontSize="small" />
                                        {t('CloseYear')}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.openYear}
                                        disabled={updating || getClosedCount() === 0}
                                        onClick={() => handleToggleYear(false)}
                                    >
                                        <LockOpenIcon fontSize="small" />
                                        {t('OpenYear')}
                                    </button>
                                </div>
                            )}

                            {loading ? (
                                <div className={styles.loading}>
                                    <CircularProgress className={styles.spinner} size={40} />
                                </div>
                            ) : (
                                <div className={styles.scroll}>
                                    <div className={styles.monthsGrid}>
                                        {monthsStatus.map((month) => (
                                            <button
                                                type="button"
                                                key={month.month}
                                                className={`${styles.monthCard} ${month.isClosed ? styles.monthCardClosed : ''}`}
                                                disabled={updating}
                                                onClick={() => handleToggleMonth(month.month, month.isClosed, getMonthName(month.month))}
                                            >
                                                <span className={styles.monthName}>{getMonthName(month.month)}</span>
                                                <span className={styles.monthMeta}>{month.month}/{selectedYear}</span>
                                                {month.isClosed ? (
                                                    <LockIcon className={styles.monthIconClosed} />
                                                ) : (
                                                    <LockOpenIcon className={styles.monthIconOpen} />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {updating && (
                            <div className={styles.updatingOverlay}>
                                <CircularProgress className={styles.spinner} size={40} />
                                <p className={styles.updatingText}>{t('Updating')}...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default MonthStatusModal;
